import { useState, createContext, useCallback, useMemo } from "react";
import MediaQuery from "../utilities/MediaQuery";
import {
  isAuthenticatedUser,
  isLoggedInUserInfo,
} from "../utilities/AuthState";

export const UserContext = createContext();

const safeParseStorageJSON = (key, fallbackValue) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return fallbackValue;
    }

    const parsed = JSON.parse(raw);
    return parsed ?? fallbackValue;
  } catch (error) {
    return fallbackValue;
  }
};

const UserContextProvider = ({ children }) => {
  const user = safeParseStorageJSON("user", {});
  const token = safeParseStorageJSON("token", null);
  const localCollection = safeParseStorageJSON("collectionId", []);
  const isAuthenticated = isAuthenticatedUser(user, token);

  const [userInformation, setUserInformation] = useState({
    userId: user.id || user.userId || "",
    userType: user.userType || "",
    vendor: user.vendor || "",
    userName: user.userName || "",
    isLoggedIn: isAuthenticated,
  });

  const [collection, setCollection] = useState(
    Array.isArray(localCollection) ? localCollection : [],
  );

  const handleLogOutUser = useCallback(() => {
    const logoutInfo = {
      userId: "",
      userType: "",
      vendor: "",
      userName: "",
      isLoggedIn: false,
    };
    setUserInformation(logoutInfo);
    localStorage.setItem("user", JSON.stringify(logoutInfo));
    localStorage.removeItem("token");
  }, []);

  const changeUserInformation = useCallback((value) => {
    const nextValue = {
      userId: value?.userId || "",
      userType: value?.userType || "",
      vendor: value?.vendor || "",
      userName: value?.userName || "",
      isLoggedIn: isLoggedInUserInfo(value),
    };

    setUserInformation(nextValue);
    localStorage.setItem("user", JSON.stringify(nextValue));
  }, []);

  const changelogOutUser = useCallback(() => {
    handleLogOutUser();
  }, [handleLogOutUser]);

  const changeCollection = useCallback((value) => {
    setCollection((previousCollection) => {
      const nextCollection = previousCollection.includes(value)
        ? previousCollection.filter((item) => item !== value)
        : [...previousCollection, value];

      localStorage.setItem("collectionId", JSON.stringify(nextCollection));
      return nextCollection;
    });
  }, []);

  const updateCollection = useCallback((value) => {
    const nextCollection = Array.isArray(value) ? value : [];
    setCollection(nextCollection);
    localStorage.setItem("collectionId", JSON.stringify(nextCollection));
  }, []);

  const media = MediaQuery();

  const value = useMemo(
    () => ({
      media,
      userInformation,
      changeUserInformation,
      changelogOutUser,
      collection,
      changeCollection,
      updateCollection,
    }),
    [
      media,
      userInformation,
      changeUserInformation,
      changelogOutUser,
      collection,
      changeCollection,
      updateCollection,
    ],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export default UserContextProvider;
